/**
 * Generates list for a dropdown from a given static data key.
 * @param {Object} staticData - The static data object containing arrays of items.
 * @param {string} key - The key in the staticData object to generate options from.
 * @param {string} valueKey - The property to use as the value for each option.
 * @param {string} labelKey - The property to use as the label for each option.
 * @returns {Array} - An array of options with value and label properties.
 */

export const createList = (staticData, key, filter = {}, valueKey = "Code", labelKey = "Description", exclude = false, sortBy) => {
    /**
   * Generates a formatted list for dropdowns from static data based on provided filters.
   *
   * @param {Object} staticData - The full static data object containing arrays of keyed data.
   * @param {string} key - The specific key in staticData from which to generate the list.
   * @param {Object} [filter={}] - Key-value pairs used to filter the list. Values can be single or arrays.
   * @param {string} [valueKey="Code"] - The property to extract as the `value` in each option.
   * @param {string} [labelKey="Description"] - The property to extract as the `label` in each option.
   * @param {boolean} [exclude=false] - If true, excludes items matching the filter instead of including them.
   * @param {string} sortBy - The key in each object used for sorting
   * @returns {Array<{value: string, label: string}>} - A list of objects suitable for use in dropdowns.
   */

    if (!staticData[key]) return [];
    return staticData[key]
        .sort((a, b) => {
            if (!sortBy) {
                sortBy = labelKey;
            }
            let valA = a[sortBy];
            let valB = b[sortBy];
            // Check if both values are strings that represent integers
            const isNumericString = (val) =>
                typeof val === "string" && /^\d+$/.test(val);

            if (isNumericString(valA) && isNumericString(valB)) {
                valA = parseInt(valA, 10);
                valB = parseInt(valB, 10);
            }

            if (typeof valA === "number" && typeof valB === "number") {
                return valA - valB;
            }

            if (typeof valA === "string" && typeof valB === "string") {
                return valA.localeCompare(valB);
            }

            return 0; // In case of undefined
        })
        .filter((item) =>
            Object.entries(filter).every(([filterKey, filterVal]) => {
                if (Array.isArray(filterVal)) {
                    return exclude
                        ? !filterVal.includes(item[filterKey])
                        : filterVal.includes(item[filterKey]);
                } else {
                    return exclude
                        ? item[filterKey] !== filterVal
                        : item[filterKey] === filterVal;
                }
            })
        )
        .map((item) => ({
            value: item[valueKey],
            label: item[labelKey],
        }));
};

export const createGroupedList = (staticData, key, valueKey = "Code", labelKey = "Description") => {

}


// MultiStep form utility functions

// Multistep form steps might contain grouped steps, which need to be flattened for rendering.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// const exampleSteps = [
//     { component: EnrollmentInfoPage, title: 'Student/Enrollment Information', condition: () => true },
//     { component: ResidencyLanguageCitizenship, title: 'Residency/ Language/ Citizenship' },
//     {
//         title: 'Information/ Consent',
//         grouped: true,
//         content: [
//             { component: APManual, title: 'Administrative Procedure Manuals' },
//             {
//                 component: KinInfoShare,
//                 title: 'Kindergarten Sharing of Information Consent Form',
//                 condition: (formData) => formData.grade === "0K"
//             },
//             { component: PreferredNamePronoun, title: 'Consent For Use of Preferred Name or Pronoun(s)' },
//         ],
//     },
//     { component: SubmitStep, title: 'Submit' },
// ];
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const flattenSteps = (steps) => {
    const flattenedSteps = [];
    steps.forEach(step => {
        if (step.grouped) {
            step.content.forEach((subStep) => {
                flattenedSteps.push({
                    ...subStep,
                    parentTitle: step.title,
                });
            });
        }
        else {
            flattenedSteps.push(step);
        }
    });

    return flattenedSteps;
}